import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { debugLog } from '../utils/debug';
import { ADULT_SKATER_LEVELS } from '../utils/skatingLevels';
import { API_BASE_URL } from '../config/environment'; // Import from existing environment config
import { collectSegmentLinksFromIndexDom } from '../utils/liveScoreIndex';
import { fetchInterclubs, fetchClubs } from '../services/clubService';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  EditNote as EditNoteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

export default function CompetitionManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getIdToken, isAdmin } = useFirebaseAuth();
  const [competitions, setCompetitions] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [interclubs, setInterclubs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [seasonInterclubFilter, setSeasonInterclubFilter] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  const currentSeasonIds = useMemo(
    () => seasons.filter((season) => season.isCurrent).map((season) => season.id),
    [seasons]
  );
  const visibleCompetitions = useMemo(() => {
    if (currentSeasonIds.length === 0) {
      return competitions;
    }
    return competitions.filter((competition) => currentSeasonIds.includes(competition.seasonId));
  }, [competitions, currentSeasonIds]);
  const sortedCompetitions = useMemo(() => {
    const toDateValue = (competition) => {
      const value = competition.startDate || competition.date;
      if (!value) return 0;
      if (value._seconds) return value._seconds * 1000;
      if (value.toDate && typeof value.toDate === 'function') return value.toDate().getTime();
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };

    const getSeasonName = (competition) => {
      const season = seasons.find((item) => item.id === competition.seasonId);
      return season?.name || '';
    };

    const sorted = [...visibleCompetitions].sort((a, b) => {
      let aValue;
      let bValue;
      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'season':
          aValue = getSeasonName(a);
          bValue = getSeasonName(b);
          break;
        case 'club':
          aValue = a.club || '';
          bValue = b.club || '';
          break;
        case 'location':
          aValue = a.location || '';
          bValue = b.location || '';
          break;
        case 'date':
        default:
          aValue = toDateValue(a);
          bValue = toDateValue(b);
          break;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }
      return String(aValue).localeCompare(String(bValue));
    });

    if (sortDirection === 'desc') {
      sorted.reverse();
    }

    return sorted;
  }, [visibleCompetitions, seasons, sortField, sortDirection]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [liveScoreProcessing, setLiveScoreProcessing] = useState(false);
  const [liveScoreMessage, setLiveScoreMessage] = useState(null);
  /** IJS block vs Majority 6.0 block on combined index pages */
  const [liveScoreSegmentSystem, setLiveScoreSegmentSystem] = useState('both');
  const [resultsViewOpen, setResultsViewOpen] = useState(false);
  const [resultsViewData, setResultsViewData] = useState([]);
  const [resultsViewLoading, setResultsViewLoading] = useState(false);
  const [resultsViewSource, setResultsViewSource] = useState('');
  const [resultsViewDocId, setResultsViewDocId] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideCsvText, setOverrideCsvText] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState(null);
  const [rowMarkSavingIdx, setRowMarkSavingIdx] = useState(null);
  const [seasonDialogOpen, setSeasonDialogOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [currentTab, setCurrentTab] = useState(0); // 0 = Competitions, 1 = Seasons
  const [seasonFormData, setSeasonFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    interclubId: '',
    interclubIds: [],
    isCurrent: false,
    registrationOpen: false,
    seasonEntryFee: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    endDate: '',
    location: '',
    club: '',
    clubId: '',
    interclubId: '',
    level: '',
    description: '',
    seasonId: '',
    liveScoreLink: '',
    registrationLink: '',
    clubWebsiteLink: ''
  });

  // Load seasons from API
  const loadSeasons = useCallback(async () => {
    try {
      const query = seasonInterclubFilter ? `?interclubId=${encodeURIComponent(seasonInterclubFilter)}` : '';
      const response = await fetch(`${API_BASE_URL}/seasons${query}`);
      if (!response.ok) {
        throw new Error('Failed to fetch seasons');
      }
      const seasonList = await response.json();
      setSeasons(seasonList);
    } catch (err) {
      console.error('Error loading seasons:', err);
    }
  }, [seasonInterclubFilter]);

  const loadInterclubs = useCallback(async () => {
    try {
      const interclubList = await fetchInterclubs();
      setInterclubs(interclubList);
    } catch (err) {
      console.error('Error loading interclubs:', err);
    }
  }, []);

  const loadClubs = useCallback(async (interclubId = '') => {
    try {
      const clubList = await fetchClubs(interclubId);
      setClubs(clubList);
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  }, []);

  // Season management functions
  const handleSeasonCreate = () => {
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      interclubId: '',
      interclubIds: [],
      isCurrent: false,
      registrationOpen: false,
      seasonEntryFee: ''
    });
    setEditingSeason(null);
    setSeasonDialogOpen(true);
  };

  const handleSeasonEdit = (season) => {
    let startDateValue = '';
    let endDateValue = '';
    // Use UTC methods to preserve the stored date without timezone shifts
    if (season.startDate?._seconds) {
      const date = new Date(season.startDate._seconds * 1000);
      startDateValue = date.getUTCFullYear() + '-' + 
                      String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                      String(date.getUTCDate()).padStart(2, '0');
    } else if (season.startDate) {
      const date = new Date(season.startDate);
      startDateValue = date.getUTCFullYear() + '-' + 
                      String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                      String(date.getUTCDate()).padStart(2, '0');
    }
    if (season.endDate?._seconds) {
      const date = new Date(season.endDate._seconds * 1000);
      endDateValue = date.getUTCFullYear() + '-' + 
                    String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                    String(date.getUTCDate()).padStart(2, '0');
    } else if (season.endDate) {
      const date = new Date(season.endDate);
      endDateValue = date.getUTCFullYear() + '-' + 
                    String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                    String(date.getUTCDate()).padStart(2, '0');
    }
    const participatingIds = Array.isArray(season.interclubIds) && season.interclubIds.length > 0
      ? season.interclubIds
      : season.interclubId ? [season.interclubId] : [];
    setSeasonFormData({
      name: season.name || '',
      startDate: startDateValue,
      endDate: endDateValue,
      interclubId: season.interclubId || participatingIds[0] || '',
      interclubIds: participatingIds,
      isCurrent: season.isCurrent || false,
      registrationOpen: season.registrationOpen === true,
      seasonEntryFee: season.seasonEntryFee ?? ''
    });
    setEditingSeason(season);
    setSeasonDialogOpen(true);
  };

  const handleSeasonDelete = async (seasonId) => {
    if (window.confirm('Are you sure you want to delete this season? This will also remove all associated competitions.')) {
      try {
        const response = await fetch(`${API_BASE_URL}/seasons/${seasonId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          await loadSeasons();
          await loadCompetitions(); // Reload competitions as they may be affected
        } else {
          throw new Error('Failed to delete season');
        }
      } catch (err) {
        console.error('Error deleting season:', err);
        alert('Failed to delete season');
      }
    }
  };

  const handleSeasonSubmit = async () => {
    try {
      const participatingIds = Array.isArray(seasonFormData.interclubIds) && seasonFormData.interclubIds.length > 0
        ? seasonFormData.interclubIds
        : seasonFormData.interclubId ? [seasonFormData.interclubId] : [];
      if (participatingIds.length === 0) {
        alert('Select at least one participating interclub.');
        return;
      }
      const hostId = seasonFormData.interclubId && participatingIds.includes(seasonFormData.interclubId)
        ? seasonFormData.interclubId
        : participatingIds[0];
      if (!hostId) {
        alert('Select the host interclub (collects fees).');
        return;
      }
      const seasonData = {
        name: seasonFormData.name,
        startDate: seasonFormData.startDate,
        endDate: seasonFormData.endDate,
        interclubId: hostId,
        interclubIds: participatingIds,
        isCurrent: seasonFormData.isCurrent,
        registrationOpen: seasonFormData.registrationOpen === true,
        seasonEntryFee: seasonFormData.seasonEntryFee !== '' ? Number(seasonFormData.seasonEntryFee) : null
      };

      let response;
      if (editingSeason) {
        // Update existing season
        response = await fetch(`${API_BASE_URL}/seasons/${editingSeason.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(seasonData)
        });
      } else {
        // Create new season
        response = await fetch(`${API_BASE_URL}/seasons`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(seasonData)
        });
      }

      if (response.ok) {
        await loadSeasons();
        setSeasonDialogOpen(false);
        setSeasonFormData({
          name: '',
          startDate: '',
          endDate: '',
          interclubId: '',
          interclubIds: [],
          isCurrent: false,
          seasonEntryFee: ''
        });
        setEditingSeason(null);
      } else {
        throw new Error('Failed to save season');
      }
    } catch (err) {
      console.error('Error saving season:', err);
      alert('Failed to save season');
    }
  };

  const handleSeasonClose = () => {
    setSeasonDialogOpen(false);
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      interclubId: '',
      interclubIds: [],
      isCurrent: false,
      registrationOpen: false,
    });
    setEditingSeason(null);
  };

  // Load competitions from API
  const loadCompetitions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/competitions`);
      if (!response.ok) {
        throw new Error('Failed to fetch competitions');
      }
      const competitionList = await response.json();
      setCompetitions(competitionList);
    } catch (err) {
      setError('Failed to load competitions');
      console.error('Error loading competitions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSeasons();
    loadCompetitions();
    loadInterclubs();
    loadClubs();
  }, [loadSeasons, loadCompetitions, loadInterclubs, loadClubs]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInterclubChange = async (event) => {
    const interclubId = event.target.value;
    setFormData(prev => ({
      ...prev,
      interclubId,
      clubId: '',
      club: '',
    }));
    if (interclubId) {
      await loadClubs(interclubId);
    } else {
      setClubs([]);
    }
  };

  const handleClubChange = (event) => {
    const clubId = event.target.value;
    const selectedClub = clubs.find((club) => club.id === clubId);
    const clubWebsite = selectedClub?.contact?.website || '';
    setFormData(prev => ({
      ...prev,
      clubId,
      club: selectedClub?.name || '',
      interclubId: selectedClub?.interclubId || prev.interclubId,
      clubWebsiteLink: prev.clubWebsiteLink || clubWebsite,
    }));
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: '',
      date: '',
      endDate: '',
      location: '',
      club: '',
      clubId: '',
      interclubId: '',
      level: '',
      description: '',
      seasonId: '',
      liveScoreLink: '',
      registrationLink: '',
      clubWebsiteLink: ''
    });
    setEditingCompetition(null);
  };

  // Open dialog for creating new competition
  const handleCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Open dialog for editing competition
  const handleEdit = (competition) => {
    debugLog('Editing competition:', competition);
    debugLog('Competition date:', competition.date);

    if (competition.interclubId) {
      loadClubs(competition.interclubId);
    }
    
    let dateValue = '';
    
    const competitionStart = competition.startDate || competition.date;
    if (competitionStart) {
      try {
        // Handle different date formats - use UTC methods to avoid timezone shifts
        if (competitionStart.toDate && typeof competitionStart.toDate === 'function') {
          // Firestore Timestamp - use UTC to preserve the stored date
          const date = competitionStart.toDate();
          dateValue = date.getUTCFullYear() + '-' + 
                     String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                     String(date.getUTCDate()).padStart(2, '0');
          debugLog('Firestore Timestamp, converted to:', dateValue);
        } else if (competitionStart._seconds !== undefined && competitionStart._nanoseconds !== undefined) {
          // Raw Firestore Timestamp object - use UTC to preserve the stored date
          const utcDate = new Date(competitionStart._seconds * 1000);
          dateValue = utcDate.getUTCFullYear() + '-' + 
                     String(utcDate.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                     String(utcDate.getUTCDate()).padStart(2, '0');
        } else if (competitionStart instanceof Date) {
          // JavaScript Date object - use UTC to preserve the stored date
          dateValue = competitionStart.getUTCFullYear() + '-' + 
                     String(competitionStart.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                     String(competitionStart.getUTCDate()).padStart(2, '0');
          debugLog('JavaScript Date, converted to:', dateValue);
        } else if (typeof competitionStart === 'string') {
          // String date - if it's YYYY-MM-DD format, use directly to avoid parsing issues
          if (/^\d{4}-\d{2}-\d{2}$/.test(competitionStart)) {
            dateValue = competitionStart;
          } else {
            const parsedDate = new Date(competitionStart);
            if (!isNaN(parsedDate.getTime())) {
              dateValue = parsedDate.getUTCFullYear() + '-' + 
                         String(parsedDate.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                         String(parsedDate.getUTCDate()).padStart(2, '0');
            }
          }
          debugLog('String date, converted to:', dateValue);
        }
      } catch (error) {
        console.error('Error parsing date:', error);
        dateValue = '';
      }
    }
    
    debugLog('Final dateValue:', dateValue);

    // Handle end date parsing - use UTC methods to avoid timezone shifts
    let endDateValue = '';
    if (competition.endDate) {
      try {
        if (competition.endDate.toDate && typeof competition.endDate.toDate === 'function') {
          // Firestore Timestamp - use UTC to preserve the stored date
          const date = competition.endDate.toDate();
          endDateValue = date.getUTCFullYear() + '-' + 
                        String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                        String(date.getUTCDate()).padStart(2, '0');
        } else if (competition.endDate._seconds !== undefined && competition.endDate._nanoseconds !== undefined) {
          // Raw Firestore Timestamp object - use UTC to preserve the stored date
          const utcDate = new Date(competition.endDate._seconds * 1000);
          endDateValue = utcDate.getUTCFullYear() + '-' + 
                        String(utcDate.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                        String(utcDate.getUTCDate()).padStart(2, '0');
        } else if (competition.endDate instanceof Date) {
          // JavaScript Date object - use UTC to preserve the stored date
          endDateValue = competition.endDate.getUTCFullYear() + '-' + 
                        String(competition.endDate.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                        String(competition.endDate.getUTCDate()).padStart(2, '0');
        } else if (typeof competition.endDate === 'string') {
          // String date - if it's YYYY-MM-DD format, use directly to avoid parsing issues
          if (/^\d{4}-\d{2}-\d{2}$/.test(competition.endDate)) {
            endDateValue = competition.endDate;
          } else {
            const parsedDate = new Date(competition.endDate);
            if (!isNaN(parsedDate.getTime())) {
              endDateValue = parsedDate.getUTCFullYear() + '-' + 
                            String(parsedDate.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                            String(parsedDate.getUTCDate()).padStart(2, '0');
            }
          }
        }
      } catch (error) {
        console.error('Error parsing end date:', error);
        endDateValue = '';
      }
    }

    const matchedClub = !competition.clubId && competition.club
      ? clubs.find((club) => club.name === competition.club)
      : null;

    setFormData({
      name: competition.name,
      date: dateValue,
      endDate: endDateValue,
      location: competition.location || '',
      club: competition.club || '',
      clubId: competition.clubId || matchedClub?.id || '',
      interclubId: competition.interclubId || matchedClub?.interclubId || '',
      level: competition.level || '',
      description: competition.description || '',
      seasonId: competition.seasonId || '',
      liveScoreLink: competition.liveScoreLink || '',
      registrationLink: competition.registrationLink || '',
      clubWebsiteLink: competition.clubWebsiteLink || ''
    });
    setEditingCompetition(competition);
    setDialogOpen(true);
  };

  // Open details dialog
  const handleViewDetails = (competition) => {
    setSelectedCompetition(competition);
    setDetailsDialogOpen(true);
  };

  // Close details dialog
  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedCompetition(null);
    setLiveScoreMessage(null);
    setResultsViewOpen(false);
  };

  const getResultCell = (row, key) => {
    if (row[key] !== undefined && row[key] !== null) return String(row[key]);
    if (key === 'Parsed group') return row.Group !== undefined && row.Group !== null ? String(row.Group) : '';
    const camel = key.replace(/\s+(\w)/g, (_, c) => c.toUpperCase()).replace(/^./, (c) => c.toLowerCase());
    return row[camel] !== undefined && row[camel] !== null ? String(row[camel]) : '';
  };

  const getEligibilityDisplay = (row) => {
    const eligibility = (getResultCell(row, 'Eligibility') || '').trim();
    if (eligibility === 'Eligible' && row.registeredForScoring === false) return 'Ineligible (not paid)';
    return eligibility || '';
  };

  const getPointsDisplay = (row) => {
    if (row.registeredForScoring === false) return '0';
    return getResultCell(row, 'Points');
  };

  const RESULTS_TABLE_HEADERS = ['Place', 'Parsed group', 'First Name', 'Last Name', 'Event', 'Level', 'Club', 'Points', 'Eligibility'];

  const resultsToCsv = (rows) => {
    const escape = (v) => (v == null ? '' : String(v).includes(',') || String(v).includes('"') ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    const headerLine = RESULTS_TABLE_HEADERS.join(',');
    const dataLines = (rows || []).map((row) =>
      RESULTS_TABLE_HEADERS.map((h) =>
        escape(h === 'Eligibility' ? getEligibilityDisplay(row) : h === 'Points' ? getPointsDisplay(row) : getResultCell(row, h))
      ).join(',')
    );
    return [headerLine, ...dataLines].join('\n');
  };

  const parseCsvToResults = (csvText) => {
    const lines = (csvText || '').trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, '').trim());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let rest = lines[i];
      let col = 0;
      while (rest.length > 0 && col < headers.length) {
        if (rest.startsWith('"')) {
          let end = 1;
          while (end < rest.length) {
            const next = rest.indexOf('"', end);
            if (next === -1) break;
            if (rest[next + 1] === '"') {
              end = next + 2;
              continue;
            }
            end = next;
            break;
          }
          values.push(rest.slice(1, end).replace(/""/g, '"'));
          rest = rest.slice(end + 1).replace(/^,/, '');
        } else {
          const comma = rest.indexOf(',');
          const val = comma === -1 ? rest : rest.slice(0, comma);
          values.push(val.trim());
          rest = comma === -1 ? '' : rest.slice(comma + 1);
        }
        col++;
      }
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = values[idx] != null ? values[idx] : '';
      });
      rows.push(obj);
    }
    return rows;
  };

  const handlePrintResults = () => window.print();

  const handleExportResults = () => {
    const csv = resultsToCsv(resultsViewData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `competition-results-${selectedCompetition?.name?.replace(/\s+/g, '-') || 'export'}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenOverride = () => {
    setOverrideCsvText(resultsToCsv(resultsViewData));
    setOverrideError(null);
    setOverrideDialogOpen(true);
  };

  const persistResultsRows = async (rows) => {
    if (!resultsViewDocId) return;
    const token = await getIdToken();
    const res = await fetch(`${API_BASE_URL}/competition-results/${resultsViewDocId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
      body: JSON.stringify({ results: rows }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to save');
    setResultsViewData(Array.isArray(data.results) ? data.results : rows);
  };

  const handleMarkRowEligible = async (idx) => {
    const raw = (getResultCell(resultsViewData[idx], 'Eligibility') || '').trim();
    if (raw === 'Eligible') return;
    setRowMarkSavingIdx(idx);
    try {
      const next = resultsViewData.map((r, i) => {
        if (i !== idx) return r;
        return { ...r, Eligibility: 'Eligible', eligibility: 'Eligible' };
      });
      // Server recalculates Points from Place + Eligibility on save
      await persistResultsRows(next);
      setLiveScoreMessage({ severity: 'success', text: 'Row marked as qualifying (Eligible); points recalculated.' });
    } catch (e) {
      setLiveScoreMessage({ severity: 'error', text: e.message || 'Failed to save' });
    } finally {
      setRowMarkSavingIdx(null);
    }
  };

  const handleSaveOverride = async () => {
    if (!resultsViewDocId) return;
    setOverrideSaving(true);
    setOverrideError(null);
    try {
      const parsed = parseCsvToResults(overrideCsvText);
      if (parsed.length === 0) {
        setOverrideError('No valid rows to save. Check CSV format (header row + data rows).');
        return;
      }
      // Server recalculates Points from Place + Eligibility on save
      await persistResultsRows(parsed);
      setOverrideDialogOpen(false);
      setLiveScoreMessage({ severity: 'success', text: 'Results updated; points recalculated from eligibility.' });
    } catch (e) {
      setOverrideError(e.message || 'Failed to save');
    } finally {
      setOverrideSaving(false);
    }
  };

  const fetchAndShowResults = async (competitionId) => {
    setResultsViewLoading(true);
    setResultsViewData([]);
    setResultsViewSource('');
    setResultsViewOpen(true);
    try {
      const res = await fetch(`${API_BASE_URL}/competition-results/${competitionId}`);
      const list = await res.json().catch(() => []);
      if (!res.ok) {
        setResultsViewData([]);
        setLiveScoreMessage({ severity: 'error', text: list?.error || 'Failed to load results' });
        return;
      }
      const latest = Array.isArray(list) ? list.find((r) => r.isLatest) || list[0] : null;
      const rows = latest?.results || [];
      setResultsViewData(rows);
      setResultsViewDocId(latest?.id || null);
      const src = latest?.source;
      setResultsViewSource(src === 'live-scores' ? 'Live score' : src === 'pdf-upload' ? 'PDF upload' : 'PDF upload');
      if (rows.length > 0) setLiveScoreMessage(null);
    } catch {
      setResultsViewData([]);
      setLiveScoreMessage({ severity: 'error', text: 'Failed to load results' });
    } finally {
      setResultsViewLoading(false);
    }
  };

  // Edit from details dialog
  const handleEditFromDetails = () => {
    if (selectedCompetition) {
      handleEdit(selectedCompetition);
      setDetailsDialogOpen(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
          debugLog('Form validation - name:', formData.name);
    debugLog('Form validation - date:', formData.date);
    debugLog('Form validation - seasonId:', formData.seasonId);
      
      if (!formData.name || !formData.date) {
        setError('Competition name and date are required');
        return;
      }

      if (!formData.clubId) {
        setError('Club is required');
        return;
      }
      
      if (!formData.seasonId) {
        setError('Season selection is required');
        return;
      }

      const url = editingCompetition 
        ? `${API_BASE_URL}/competitions/${editingCompetition.id}`
        : `${API_BASE_URL}/competitions`;
      
      const method = editingCompetition ? 'PUT' : 'POST';
      
      debugLog('Sending data:', formData);
      debugLog('Request URL:', url);
      debugLog('Request method:', method);

      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      
              debugLog('Response status:', response.status);
        debugLog('Response status text:', response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        console.error('Response headers:', response.headers);
        throw new Error(`Failed to save competition: ${response.status} ${response.statusText} - ${errorText}`);
      }

      setDialogOpen(false);
      resetForm();
      loadCompetitions();
      setError(null);
    } catch (err) {
      setError('Failed to save competition');
      console.error('Error saving competition:', err);
    }
  };

  // Handle competition deletion
  const handleDelete = async (competition) => {
    if (!window.confirm(`Are you sure you want to delete "${competition.name}"?`)) {
      return;
    }

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/competitions/${competition.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete competition');
      }

      loadCompetitions();
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting competition:', err);
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not set';
    
    try {
      let dateObj;
      
      if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp - preserve local date
        dateObj = date.toDate();
      } else if (date._seconds !== undefined && date._nanoseconds !== undefined) {
        // Raw Firestore Timestamp object - extract exact date components
        const utcDate = new Date(date._seconds * 1000);
        // Format directly without creating a new Date object to avoid timezone issues
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[utcDate.getUTCMonth()]} ${utcDate.getUTCDate()}, ${utcDate.getUTCFullYear()}`;
      } else if (date instanceof Date) {
        // JavaScript Date object - extract exact date components
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      } else if (typeof date === 'string') {
        // String date - try to parse it and extract exact components
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) {
          return 'Invalid Date';
        }
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parsedDate.getMonth()]} ${parsedDate.getDate()}, ${parsedDate.getFullYear()}`;
      } else {
        return 'Invalid Date';
      }
      
      // For Firestore Timestamps, format directly
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
    } catch (error) {
      console.error('❌ Error formatting date:', error);
      return 'Invalid Date';
    }
  };



  // Close dialog
  const handleClose = () => {
    setDialogOpen(false);
    resetForm();
    setError(null);
  };

  // Mobile-friendly competition list item component
  const CompetitionListItem = ({ competition, index }) => {
    const season = seasons.find(s => s.id === competition.seasonId);
    
    return (
      <Paper
        elevation={1}
        sx={{
          mb: 1,
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={() => handleViewDetails(competition)}
      >
        <Box sx={{ p: 2 }}>
          {/* Main row with name and season */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', minWidth: '2rem' }}>
                #{index + 1}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {competition.name}
              </Typography>
            </Box>
            <Chip 
              label={season?.name || 'No Season'} 
              size="small" 
              color={season?.isCurrent ? 'success' : 'default'}
              variant={season?.isCurrent ? 'filled' : 'outlined'}
              title={season?.isCurrent ? 'Current Season' : 'Previous Season'}
            />
          </Box>
          
          {/* Secondary row with dates and location */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                {formatDate(competition.startDate || competition.date)}
              </Typography>
              {competition.endDate && (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    →
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                    {formatDate(competition.endDate)}
                  </Typography>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {competition.location || 'No location'}
            </Typography>
          </Box>

          {/* Third row with club and level */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {competition.club || 'No club'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                •
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {competition.level || 'All Levels'}
              </Typography>
            </Box>
          </Box>
          
          {/* Fourth row with action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(competition);
              }}
              title="Edit competition"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {competition.liveScoreLink && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                href={competition.liveScoreLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                📊 View Live Scores
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
            🏆 Competition Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                color="primary"
                size={isMobile ? "small" : "medium"}
              >
                Add Competition
              </Button>
            )}
            {currentTab === 1 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleSeasonCreate}
                color="primary"
                size={isMobile ? "small" : "medium"}
              >
                Add Season
              </Button>
            )}
            <IconButton 
              onClick={currentTab === 0 ? loadCompetitions : loadSeasons} 
              color="primary" 
              title={currentTab === 0 ? "Refresh competitions" : "Refresh seasons"}
              size={isMobile ? "small" : "medium"}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Competitions" />
            <Tab label="Seasons" />
          </Tabs>
        </Box>

        {/* Tab Content: Seasons */}
        {currentTab === 1 && (
          <Box>
            <Box sx={{ mb: 2, maxWidth: 320 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Interclub</InputLabel>
                <Select
                  value={seasonInterclubFilter}
                  label="Filter by Interclub"
                  onChange={(e) => setSeasonInterclubFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All interclubs</em>
                  </MenuItem>
                  {interclubs.map((interclub) => (
                    <MenuItem key={interclub.id} value={interclub.id}>
                      {interclub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            {seasons.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Season Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date Range</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Interclub</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {seasons.map((season) => (
                      <TableRow key={season.id} hover>
                        <TableCell>{season.name}</TableCell>
                        <TableCell>
                          {season.startDate?._seconds
                            ? new Date(season.startDate._seconds * 1000).toLocaleDateString()
                            : season.startDate
                            ? new Date(season.startDate).toLocaleDateString()
                            : season.startYear || '—'}
                          {' '}
                          -
                          {' '}
                          {season.endDate?._seconds
                            ? new Date(season.endDate._seconds * 1000).toLocaleDateString()
                            : season.endDate
                            ? new Date(season.endDate).toLocaleDateString()
                            : season.endYear || '—'}
                        </TableCell>
                        <TableCell>
                          {Array.isArray(season.interclubIds) && season.interclubIds.length > 1
                            ? season.interclubIds
                                .map((id) => interclubs.find((i) => i.id === id)?.name || id)
                                .join(', ')
                            : (interclubs.find((i) => i.id === season.interclubId)?.name || '—')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={season.isCurrent ? 'Current' : 'Past'}
                            color={season.isCurrent ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSeasonEdit(season)}
                              title="Edit season"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleSeasonDelete(season.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No seasons found. Create your first season to get started.
              </Typography>
            )}
          </Box>
        )}


        {/* Tab Content: Competitions */}
        {currentTab === 0 && (
          <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              🏁 Competitions
            </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="competition-sort-label">Sort By</InputLabel>
              <Select
                labelId="competition-sort-label"
                label="Sort By"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              >
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="season">Season</MenuItem>
                <MenuItem value="club">Club</MenuItem>
                <MenuItem value="location">Location</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="competition-sort-direction-label">Direction</InputLabel>
              <Select
                labelId="competition-sort-direction-label"
                label="Direction"
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        {isMobile ? (
          // Mobile: Compact list view
          <Box>
            {sortedCompetitions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                <Typography variant="body2" color="text.secondary">
                  No competitions found. Create your first competition!
                </Typography>
              </Box>
            ) : (
              sortedCompetitions.map((competition, index) => (
                <CompetitionListItem key={competition.id} competition={competition} index={index} />
              ))
            )}
          </Box>
        ) : (
          // Desktop: Table view
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Season</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Club</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Live Scores</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCompetitions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No competitions found. Create your first competition!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedCompetitions.map((competition) => (
                    <TableRow 
                      key={competition.id} 
                      hover
                      onClick={() => handleViewDetails(competition)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {competition.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={competition.season?.name || 'No Season'} 
                          size="small" 
                          color={competition.season?.isCurrent ? 'success' : 'default'}
                          variant={competition.season?.isCurrent ? 'filled' : 'outlined'}
                          title={competition.season?.isCurrent ? 'Current Season' : 'Previous Season'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatDate(competition.startDate || competition.date)} 
                          size="small" 
                          icon={<CalendarIcon />}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={competition.endDate ? formatDate(competition.endDate) : 'Single Day'} 
                          size="small" 
                          icon={<CalendarIcon />}
                          variant="outlined"
                          color={competition.endDate ? "primary" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {competition.location || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {competition.club || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={competition.level || 'All Levels'} 
                          size="small" 
                          color="primary"
                          icon={<GroupIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        {competition.liveScoreLink ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            href={competition.liveScoreLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            View Live Scores
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            No link
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {competition.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(competition);
                            }}
                            color="primary"
                            title="Edit competition"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(competition);
                            }}
                            color="error"
                            title="Delete competition"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
          </>
        )}
      </Paper>

      {/* Competition Form Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleClose} 
        fullScreen={isMobile}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            width: { xs: '95%', sm: 'auto' },
            maxWidth: { xs: '95%', sm: 'md' }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 2, 
            bgcolor: 'info.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CalendarIcon sx={{ color: 'info.dark' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingCompetition ? 'Edit Competition' : 'Create New Competition'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editingCompetition ? 'Update competition details' : 'Add a new competition event'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mt: { xs: 1, sm: 2 } }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Competition Name *"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date *"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                  helperText="Leave empty for single-day events"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Season *</InputLabel>
                  <Select
                    value={formData.seasonId}
                    label="Season *"
                    onChange={(e) => handleInputChange('seasonId', e.target.value)}
                    required
                  >
                    <MenuItem value="">
                      <em>Choose a season...</em>
                    </MenuItem>
                    {seasons.map((season) => (
                      <MenuItem key={season.id} value={season.id}>
                        {season.name} {season.isCurrent && '(Current)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="e.g., McCall, ID"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Interclub</InputLabel>
                  <Select
                    value={formData.interclubId}
                    label="Interclub"
                    onChange={handleInterclubChange}
                  >
                    <MenuItem value="">
                      <em>Select an interclub...</em>
                    </MenuItem>
                    {interclubs.map((interclub) => (
                      <MenuItem key={interclub.id} value={interclub.id}>
                        {interclub.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }} disabled={!formData.interclubId}>
                  <InputLabel>Club</InputLabel>
                  <Select
                    value={formData.clubId}
                    label="Club"
                    onChange={handleClubChange}
                  >
                    <MenuItem value="">
                      <em>Select a club...</em>
                    </MenuItem>
                    {clubs.map((club) => (
                      <MenuItem key={club.id} value={club.id} sx={{ alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {club.name}
                          </Typography>
                          {club.aliases?.length ? (
                            <Typography variant="caption" color="text.secondary">
                              Includes: {club.aliases.join(', ')}
                            </Typography>
                          ) : null}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={formData.level}
                    label="Level"
                    onChange={(e) => handleInputChange('level', e.target.value)}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    <MenuItem value="Snowplow Sam">Snowplow Sam</MenuItem>
                    <MenuItem value="Basic 1">Basic 1</MenuItem>
                    <MenuItem value="Basic 2">Basic 2</MenuItem>
                    <MenuItem value="Basic 3">Basic 3</MenuItem>
                    <MenuItem value="Basic 4">Basic 4</MenuItem>
                    <MenuItem value="Basic 5">Basic 5</MenuItem>
                    <MenuItem value="Basic 6">Basic 6</MenuItem>
                    <MenuItem value="Aspire 1">Aspire 1</MenuItem>
                    <MenuItem value="Aspire 2">Aspire 2</MenuItem>
                    <MenuItem value="Aspire 3">Aspire 3</MenuItem>
                    <MenuItem value="Aspire 4">Aspire 4</MenuItem>
                    <MenuItem value="Pre-Preliminary">Pre-Preliminary</MenuItem>
                    {ADULT_SKATER_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Additional details about the competition..."
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Registration Link"
                  value={formData.registrationLink}
                  onChange={(e) => handleInputChange('registrationLink', e.target.value)}
                  placeholder="https://example.com/register"
                  helperText="Optional: Link to registration page"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Club Website Link"
                  value={formData.clubWebsiteLink}
                  onChange={(e) => handleInputChange('clubWebsiteLink', e.target.value)}
                  placeholder="https://example.com/club"
                  helperText="Optional: Host club website"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Live Score Link"
                  value={formData.liveScoreLink}
                  onChange={(e) => handleInputChange('liveScoreLink', e.target.value)}
                  placeholder="https://example.com/live-scores"
                  helperText="Optional: Link to live scoring website or results page"
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleClose}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name || !formData.date || !formData.seasonId || !formData.clubId}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {editingCompetition ? 'Save Changes' : 'Create Competition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Season Management Dialog */}
      <Dialog 
        open={seasonDialogOpen} 
        onClose={handleSeasonClose} 
        fullScreen={isMobile}
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '95%', sm: 'auto' },
            maxWidth: { xs: '95%', sm: 'sm' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {editingSeason ? 'Edit Season' : 'Create New Season'}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mt: { xs: 1, sm: 2 } }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Season Name *"
                  value={seasonFormData.name}
                  onChange={(e) => setSeasonFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="e.g., 2024-2025 Season"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date *"
                  type="date"
                  value={seasonFormData.startDate}
                  onChange={(e) => setSeasonFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date *"
                  type="date"
                  value={seasonFormData.endDate}
                  onChange={(e) => setSeasonFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Participating interclubs *</InputLabel>
                  <Select
                    multiple
                    value={seasonFormData.interclubIds?.length ? seasonFormData.interclubIds : (seasonFormData.interclubId ? [seasonFormData.interclubId] : [])}
                    label="Participating interclubs *"
                    onChange={(e) => {
                      const ids = e.target.value;
                      const next = Array.isArray(ids) ? ids : [ids];
                      setSeasonFormData((prev) => {
                        const currentHost = prev.interclubId;
                        const hostStillValid = currentHost && next.includes(currentHost);
                        return {
                          ...prev,
                          interclubIds: next,
                          interclubId: hostStillValid ? currentHost : (next[0] || '')
                        };
                      });
                    }}
                    renderValue={(selected) =>
                      (selected || []).map((id) => interclubs.find((i) => i.id === id)?.name || id).join(', ')
                    }
                    required
                  >
                    {interclubs.map((interclub) => (
                      <MenuItem key={interclub.id} value={interclub.id}>
                        {interclub.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    All interclubs whose clubs and skaters may participate in this series.
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Host interclub (collects fees) *</InputLabel>
                  <Select
                    value={seasonFormData.interclubId || ''}
                    label="Host interclub (collects fees) *"
                    onChange={(e) => setSeasonFormData((prev) => ({ ...prev, interclubId: e.target.value }))}
                    required
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select host...</em>
                    </MenuItem>
                    {(seasonFormData.interclubIds?.length ? seasonFormData.interclubIds : (seasonFormData.interclubId ? [seasonFormData.interclubId] : []))
                      .map((id) => interclubs.find((i) => i.id === id))
                      .filter(Boolean)
                      .map((interclub) => (
                        <MenuItem key={interclub.id} value={interclub.id}>
                          {interclub.name}
                        </MenuItem>
                      ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Season entry fees and Stripe billing use this interclub&apos;s account. Must be one of the participating interclubs above.
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Season Entry Fee Override (USD)"
                  type="number"
                  value={seasonFormData.seasonEntryFee}
                  onChange={(e) => setSeasonFormData(prev => ({ ...prev, seasonEntryFee: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Leave blank to use the interclub default fee."
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={seasonFormData.isCurrent}
                        onChange={(e) => setSeasonFormData(prev => ({ ...prev, isCurrent: e.target.checked }))}
                      />
                    }
                    label="Mark as Current Season"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={seasonFormData.registrationOpen === true}
                        onChange={(e) => setSeasonFormData((prev) => ({ ...prev, registrationOpen: e.target.checked }))}
                      />
                    }
                    label="Open season registration"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Leave off until the new season is ready. Families can still create accounts and view history.
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={handleSeasonClose}
            size={isMobile ? "small" : "medium"}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSeasonSubmit} 
            variant="contained"
            disabled={!seasonFormData.name || !seasonFormData.startDate || !seasonFormData.endDate || ((seasonFormData.interclubIds?.length || 0) === 0 && !seasonFormData.interclubId) || !seasonFormData.interclubId}
            size={isMobile ? "small" : "medium"}
          >
            {editingSeason ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Competition Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Competition Details
          </Typography>
          <IconButton
            size="small"
            onClick={handleCloseDetails}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedCompetition && (
            <Box>
              {/* Competition Name */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {selectedCompetition.name}
                </Typography>
                <Chip 
                  label={seasons.find(s => s.id === selectedCompetition.seasonId)?.name || 'No Season'} 
                  color={seasons.find(s => s.id === selectedCompetition.seasonId)?.isCurrent ? 'success' : 'default'}
                  variant={seasons.find(s => s.id === selectedCompetition.seasonId)?.isCurrent ? 'filled' : 'outlined'}
                />
              </Box>

              {/* Date Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarIcon color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Start Date
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {formatDate(selectedCompetition.startDate || selectedCompetition.date)}
                  </Typography>
                </Grid>
                {selectedCompetition.endDate && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        End Date
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {formatDate(selectedCompetition.endDate)}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Location & Club */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedCompetition.location || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Host Club
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedCompetition.club || 'Not specified'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Level */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Level
                </Typography>
                <Chip 
                  label={selectedCompetition.level || 'All Levels'} 
                  color="primary"
                  icon={<GroupIcon />}
                />
              </Box>

              {/* Description */}
              {selectedCompetition.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompetition.description}
                  </Typography>
                </Box>
              )}

              {/* Links */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedCompetition.registrationLink && (
                  <Button
                    variant="outlined"
                    color="primary"
                    href={selectedCompetition.registrationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    📝 Registration Link
                  </Button>
                )}
                {selectedCompetition.clubWebsiteLink && (
                  <Button
                    variant="outlined"
                    color="primary"
                    href={selectedCompetition.clubWebsiteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    🌐 Club Website
                  </Button>
                )}
                {selectedCompetition.liveScoreLink && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      href={selectedCompetition.liveScoreLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      📊 Live Scores
                    </Button>
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <InputLabel id="live-segment-system-label">Live score segments</InputLabel>
                      <Select
                        labelId="live-segment-system-label"
                        label="Live score segments"
                        value={liveScoreSegmentSystem}
                        onChange={(e) => setLiveScoreSegmentSystem(e.target.value)}
                      >
                        <MenuItem value="both">All (IJS + Majority 6.0)</MenuItem>
                        <MenuItem value="ijs">IJS only (above &quot;Majority 6.0&quot;)</MenuItem>
                        <MenuItem value="60">Majority 6.0 only</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={liveScoreProcessing}
                      onClick={async () => {
                        if (!selectedCompetition?.id || !selectedCompetition?.liveScoreLink) return;
                        setLiveScoreMessage(null);
                        setLiveScoreProcessing(true);
                        try {
                          const token = await getIdToken();
                          const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
                          const liveScoreLink = selectedCompetition.liveScoreLink.trim();
                          const indexProxyRes = await fetch(
                            `${API_BASE_URL}/proxy-live-score?url=${encodeURIComponent(liveScoreLink)}`,
                            { headers: authHeaders }
                          );
                          const indexData = await indexProxyRes.json().catch(() => ({}));
                          if (!indexProxyRes.ok) {
                            setLiveScoreMessage({ severity: 'error', text: indexData.error || 'Could not load index page.' });
                            return;
                          }
                          const indexHtml = indexData.html || '';
                          const parser = new DOMParser();
                          const doc = parser.parseFromString(indexHtml, 'text/html');
                          const segmentEntries = collectSegmentLinksFromIndexDom(
                            doc,
                            liveScoreLink,
                            liveScoreSegmentSystem
                          );
                          if (segmentEntries.length === 0) {
                            setLiveScoreMessage({ severity: 'error', text: 'No segment links found on the index page.' });
                            return;
                          }
                          const segmentHtmls = [];
                          const batchSize = 8;
                          for (let i = 0; i < segmentEntries.length; i += batchSize) {
                            setLiveScoreMessage({
                              severity: 'info',
                              text: `Fetching segment pages ${Math.min(i + batchSize, segmentEntries.length)}/${segmentEntries.length}…`,
                            });
                            const batch = segmentEntries.slice(i, i + batchSize);
                            const pages = await Promise.all(
                              batch.map(async ({ url, label }) => {
                                try {
                                  const r = await fetch(
                                    `${API_BASE_URL}/proxy-live-score?url=${encodeURIComponent(url)}`,
                                    { headers: authHeaders }
                                  );
                                  const d = await r.json().catch(() => ({}));
                                  return { url, label, html: r.ok && d.html ? d.html : '' };
                                } catch {
                                  return { url, label, html: '' };
                                }
                              })
                            );
                            segmentHtmls.push(...pages);
                          }
                          setLiveScoreMessage({
                            severity: 'info',
                            text: `Processing ${segmentHtmls.length} segment pages…`,
                          });
                          const res = await fetch(`${API_BASE_URL}/process-live-scores-from-html`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              ...authHeaders,
                            },
                            body: JSON.stringify({
                              competitionId: selectedCompetition.id,
                              competitionName: selectedCompetition.name,
                              segmentHtmls,
                            }),
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) {
                            const fallback =
                              res.status === 413
                                ? 'This competition is too large to process in one request. Try IJS only or Majority 6.0 only.'
                                : `Failed to process live scores (${res.status})`;
                            setLiveScoreMessage({ severity: 'error', text: data.error || fallback });
                            return;
                          }
                          setLiveScoreMessage({
                            severity: 'success',
                            text: data.message || `Ingested ${data.resultsCount ?? 0} results. Same qualification rules as PDF uploads.`,
                          });
                          await fetchAndShowResults(selectedCompetition.id);
                        } catch (err) {
                          setLiveScoreMessage({
                            severity: 'error',
                            text: err.message || 'Failed to process live scores',
                          });
                        } finally {
                          setLiveScoreProcessing(false);
                        }
                      }}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      startIcon={liveScoreProcessing ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {liveScoreProcessing ? 'Processing…' : 'Process scores from live site'}
                    </Button>
                  </>
                )}
                {liveScoreMessage && (
                  <Alert
                    severity={liveScoreMessage.severity}
                    onClose={() => setLiveScoreMessage(null)}
                    sx={{ mt: 1 }}
                  >
                    {liveScoreMessage.text}
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={resultsViewLoading}
                  onClick={() => selectedCompetition?.id && fetchAndShowResults(selectedCompetition.id)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', mt: 1 }}
                >
                  {resultsViewLoading ? 'Loading…' : 'View parsed results'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDetails}>
            Close
          </Button>
          <Button 
            onClick={handleEditFromDetails} 
            variant="contained"
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parsed results view dialog */}
      <Dialog
        open={resultsViewOpen}
        onClose={() => setResultsViewOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Parsed results</DialogTitle>
        <DialogContent dividers>
          {resultsViewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : resultsViewData.length === 0 ? (
            <Typography color="text.secondary">No results to display. Process scores from the live site or upload a file first.</Typography>
          ) : (
            <>
              {resultsViewSource && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Source: {resultsViewSource}
                </Typography>
              )}
              {(() => {
                const eligibleRows = resultsViewData.filter(
                  (row) => (getResultCell(row, 'Eligibility') || '').trim() === 'Eligible'
                );
                const eligibleCount = eligibleRows.length;
                const registeredForScoreCount = eligibleRows.filter((row) => row.registeredForScoring === true).length;
                const notRegisteredCount = eligibleCount - registeredForScoreCount;
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Summary: {resultsViewData.length} total placement rows (a skater may appear in multiple ineligible events).
                    {eligibleCount} eligible placements at this competition (at most one eligible event per skater).
                    Of those, <strong>{registeredForScoreCount} are registered for the season and receive a score</strong>;
                    {notRegisteredCount > 0 ? (
                      <> {notRegisteredCount} are not registered (or had not paid by the competition date) and do not count toward qualifiers.</>
                    ) : (
                      <> all are registered.</>
                    )}
                  </Typography>
                );
              })()}
              <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Place</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Parsed group</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>First Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Club</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Eligibility</TableCell>
                    {isAdmin() && resultsViewDocId && (
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultsViewData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{getResultCell(row, 'Place')}</TableCell>
                      <TableCell>{getResultCell(row, 'Parsed group')}</TableCell>
                      <TableCell>{getResultCell(row, 'First Name')}</TableCell>
                      <TableCell>{getResultCell(row, 'Last Name')}</TableCell>
                      <TableCell>{getResultCell(row, 'Event')}</TableCell>
                      <TableCell>{getResultCell(row, 'Level')}</TableCell>
                      <TableCell>{getResultCell(row, 'Club')}</TableCell>
                      <TableCell>{getPointsDisplay(row)}</TableCell>
                      <TableCell>{getEligibilityDisplay(row)}</TableCell>
                      {isAdmin() && resultsViewDocId && (
                        <TableCell align="right">
                          {(getResultCell(row, 'Eligibility') || '').trim() !== 'Eligible' && (
                            <Tooltip title="Mark this row as qualifying (Eligible). Use when the venue shortened the event title; full CSV override is still available.">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkRowEligible(idx)}
                                  disabled={rowMarkSavingIdx !== null}
                                  color="primary"
                                  aria-label="Mark row as qualifying"
                                >
                                  {rowMarkSavingIdx === idx ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button startIcon={<PrintIcon />} onClick={handlePrintResults} disabled={resultsViewData.length === 0}>
            Print
          </Button>
          <Button startIcon={<FileDownloadIcon />} onClick={handleExportResults} disabled={resultsViewData.length === 0}>
            Export CSV
          </Button>
          {isAdmin() && resultsViewDocId && (
            <Button startIcon={<EditNoteIcon />} onClick={handleOpenOverride} disabled={resultsViewData.length === 0} color="secondary">
              Override results
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setResultsViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Admin override: edit results as CSV */}
      <Dialog open={overrideDialogOpen} onClose={() => !overrideSaving && setOverrideDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Override results</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit the CSV below to correct errors, then Save. Columns: Place, Place received, Parsed group, First Name, Last Name, Event, Level, Club, Points, Eligibility.
          </Typography>
          {overrideError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOverrideError(null)}>
              {overrideError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            value={overrideCsvText}
            onChange={(e) => setOverrideCsvText(e.target.value)}
            placeholder="Place, Parsed group, First Name, Last Name, Event, Level, Club, Points, Eligibility"
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)} disabled={overrideSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveOverride} disabled={overrideSaving}>
            {overrideSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
